import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import * as adminServerSecretsService from '../../services/adminServerSecrets.service';

/**
 * A no-terminal, no-code way to hand a live third-party credential
 * straight to the server, without Claude ever seeing or typing it.
 * Built 2026-09-02 — same guarantee as /home/team365/set-secret.sh (the
 * terminal version), just as a plain in-app form for days when even a
 * single terminal command isn't workable.
 *
 * ONE PASTE — a single box, not one field per value. The first version of
 * this page had separate Account SID / Auth Token inputs, which broke an
 * already-established "one paste, no command" requirement — Jenn caught
 * it same day and it was rebuilt immediately. Everything comes from one
 * copy off Twilio's own page, pasted here once.
 *
 * Lives inside Team365's BackEnd page as its own sub-tab, only shown to
 * whoever has can_manage_server_secrets (migration 101).
 */
function SecretCard({ service, title, instructions, placeholder }) {
  const [block, setBlock] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // { ok: true, message } | { ok: false, message }

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setResult(null);
    try {
      const data = await adminServerSecretsService.saveSecret(service, block);
      setResult({ ok: true, message: data.message });
      setBlock('');
    } catch (err) {
      setResult({ ok: false, message: err.response?.data?.error || 'Could not save this. Nothing was changed — safe to try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6">
      <h2 className="text-sm font-bold text-reset-brown mb-2">{title}</h2>
      <p className="text-xs text-gray-500 mb-4">{instructions}</p>
      <form onSubmit={handleSave} className="space-y-4">
        <textarea
          value={block}
          onChange={e => setBlock(e.target.value)}
          onPaste={e => {
            // Chrome's own password manager can intercept/clear a pasted
            // value in a field it decides looks like a credential, even
            // with autoComplete="off" (Chrome deliberately ignores that
            // for login-like fields). Reading the clipboard directly here
            // and setting state from it bypasses that interception.
            const pasted = e.clipboardData?.getData('text');
            if (pasted) {
              e.preventDefault();
              setBlock(pasted);
            }
          }}
          placeholder={placeholder}
          rows={8}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          autoComplete="new-password"
          data-lpignore="true"
          data-1p-ignore="true"
          required
        />
        <p className="text-xs text-gray-400">
          {block.length === 0
            ? 'Nothing entered yet.'
            : `${block.length} character${block.length === 1 ? '' : 's'} in this box right now.`}
        </p>

        {result && (
          <div className={`text-sm px-3 py-2 rounded-lg ${result.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {result.message}
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          className="bg-reset-terracotta text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>
    </div>
  );
}

/**
 * Stripe updates (Jenn, 2026-10-07). One button: our server asks Stripe to
 * set up the webhook and keeps its key itself — nothing to copy or paste.
 * Without it Stripe can't tell us about joins, cancellations or payments.
 */
function StripeConnectCard() {
  const [status, setStatus] = useState(null);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState('');

  const load = () => adminServerSecretsService.getStripeWebhookStatus()
    .then(setStatus)
    .catch(() => setStatus({ error: true }));
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setWorking(true);
    setMessage('');
    try {
      await adminServerSecretsService.connectStripeWebhook();
      setMessage('Connected. Stripe will now tell 365reset about new members, cancellations and payments.');
      await load();
    } catch (err) {
      setMessage(err.response?.data?.error || 'That didn\'t work. Please try again.');
    }
    setWorking(false);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <h2 className="text-sm font-bold text-reset-brown mb-2">Stripe updates</h2>
      <p className="text-sm text-gray-600 mb-3">
        Lets Stripe tell 365reset when someone joins, cancels or pays — so new members get their account and welcome email, and cancellations take effect here too. Nothing to copy or paste.
      </p>
      {status === null && <p className="text-sm text-gray-400">Checking…</p>}
      {status?.error && <p className="text-sm text-gray-500">Couldn't check right now.</p>}
      {status && !status.error && (
        <p className={`text-sm mb-3 ${status.connected ? 'text-reset-sage font-medium' : 'text-gray-600'}`}>
          {status.connected ? '✓ Connected' : 'Not connected yet.'}
        </p>
      )}
      {message && <p className="text-sm text-gray-600 mb-3">{message}</p>}
      <button
        type="button"
        onClick={connect}
        disabled={working || status === null}
        className="bg-reset-terracotta text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50"
      >
        {working ? 'Connecting…' : status?.connected ? 'Reconnect Stripe' : 'Connect Stripe'}
      </button>
    </div>
  );
}

export default function AdminServerSecrets({ embedded = false }) {
  const content = (
    <div className="max-w-2xl space-y-6">
      {!embedded && <h1 className="font-serif text-2xl font-bold text-reset-brown mb-1">Server Credentials</h1>}
      <p className="text-sm text-gray-500">
        One paste. Nothing here is ever seen by Claude.
      </p>

      <StripeConnectCard />

      <SecretCard
        service="twilio"
        title="Twilio (SMS)"
        instructions={'On Twilio\'s Console home page, select from "Account SID" down through the revealed Auth Token, copy, and paste the whole thing below.'}
        placeholder={'Account SID\nACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx\n\nAuth Token\nxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'}
      />

      <SecretCard
        service="stripe"
        title="Stripe (publishable key)"
        instructions={'On Stripe → Developers → API keys, copy the "Publishable key" (starts with pk_live_) and just paste it below — nothing else needed.'}
        placeholder={'pk_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'}
      />

      <SecretCard
        service="stripe_secret"
        title="Stripe (secret key)"
        instructions={'On Stripe → Developers → API keys, next to "Secret key" choose ••• → Roll key, then copy the new key (starts with sk_live_) and paste it below. The app restarts by itself (about 40 seconds) and uses the new key straight away.'}
        placeholder={'sk_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'}
      />

      <SecretCard
        service="bunny_token"
        title="Bunny (video lock key)"
        instructions={'Makes every video members-only. In Bunny: Stream → your video library → Security. Copy the key on the "Token authentication key" card and paste it below. The app restarts by itself (about 40 seconds). Then, on that same Security page, switch on "Embed view token authentication".'}
        placeholder={'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'}
      />

      <SecretCard
        service="render"
        title="Render (API key)"
        instructions={'On Render → Account Settings → API Keys, create or copy a key (starts with rnd_) and just paste it below — nothing else needed.'}
        placeholder={'rnd_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'}
      />

      <SecretCard
        service="old_encryption_key"
        title="Old encryption key (one-time member recovery)"
        instructions={'Only if Claude has asked for this specifically. A 64-character string of just letters a-f and numbers — paste it below exactly as found. This does not change anything live or restart the app.'}
        placeholder={'e.g. 4f9a2c1b8e7d3f0a5c6b9e2d1a8f4c7b0e3d6a9c2f5b8e1d4a7c0f3b6e9d2a5c'}
      />
    </div>
  );

  if (embedded) return content;
  return <DashboardLayout>{content}</DashboardLayout>;
}
